import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Users, School, ChefHat, Building2, Landmark, Zap, MapPin, HeartHandshake, Gift, Rocket, Heart, Award, SkipForward, RotateCcw } from 'lucide-react';
import HeaderEN from '../components/HeaderEN';
import '../styles/index.css';

const HomeEN = () => {
  const navigate = useNavigate();
  const [showIntro, setShowIntro] = useState(true);
  const [introEnded, setIntroEnded] = useState(false);
  const introRef = useRef(null);
  const sectionVideoRef = useRef(null);

  useEffect(() => { document.title = "EcoLunch - Quebec School Meal Management Platform"; }, []);

  const skipIntro = () => {
    setShowIntro(false);
    if (introRef.current) introRef.current.pause();
  };

  const replayIntro = () => {
    setShowIntro(true);
    setIntroEnded(false);
    if (introRef.current) {
      introRef.current.currentTime = 0;
      introRef.current.play();
    }
  };

  return (
    <div className="home-container">
      {showIntro && !introEnded && (
        <div className="video-intro-overlay" data-testid="video-intro-en">
          <video ref={introRef} autoPlay muted playsInline onEnded={() => { setIntroEnded(true); setShowIntro(false); }} className="video-intro-player">
            <source src="/intro-ecolunch.mp4" type="video/mp4" />
          </video>
          <button className="video-intro-skip" onClick={skipIntro}>
            <SkipForward size={16} /> Skip intro
          </button>
        </div>
      )}

      <HeaderEN currentPage="home" lang="en" />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <Zap size={16} />
            <span>Unified Management Platform - Quebec Innovation</span>
          </div>
          <div className="hero-logo-title animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <img src={require('../assets/logo-ecolunch-white.png')} alt="EcoLunch" className="hero-main-logo" />
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.15s', color: '#FFFFFF', textShadow: '0 0 10px rgba(255,255,255,0.8), 0 0 30px rgba(132,204,22,0.5), 0 0 60px rgba(132,204,22,0.3)' }}>
            The Quebec school meal management platform
          </h1>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.15rem', maxWidth: '700px', margin: '0 auto' }}>
            Order, manage, automate and supervise your school meals — all in one place.
          </p>
        </div>
      </section>

      {/* Video Presentation */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container">
          <div className="video-section" data-testid="video-presentation-en">
            <video ref={sectionVideoRef} controls playsInline className="video-presentation-player">
              <source src="/presentation-ecolunch.mp4" type="video/mp4" />
            </video>
          </div>
          {introEnded || !showIntro ? (
            <div style={{textAlign: 'center', marginTop: '1rem'}}>
              <button className="video-replay-btn" onClick={replayIntro}>
                <RotateCcw size={14} /> Replay intro
              </button>
            </div>
          ) : null}
        </div>
      </section>

      {/* Problem vs Solution */}
      <section id="solution" className="section is-not-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Understanding EcoLunch and its Unified Ecosystem</h2>
            <p className="section-subtitle">
              The difference between a unified ecosystem and fragmented solutions
            </p>
          </div>

          <div className="is-not-grid">
            <div className="is-not-card not-card">
              <div className="is-not-icon">❌</div>
              <h3>What we are NOT</h3>
              <ul>
                <li>An "Uber Eats" for schools</li>
                <li>A caterer marketplace</li>
                <li>Separate websites</li>
                <li>Simple online ordering</li>
                <li>Fragile third-party integrations</li>
              </ul>
            </div>

            <div className="is-not-card is-card">
              <div className="is-not-icon">✅</div>
              <h3>What we ARE</h3>
              <ul>
                <li><strong>ONE centralized ERP/SaaS system</strong></li>
                <li><strong>FIVE portals in ONE platform</strong></li>
                <li>4-level hierarchical architecture</li>
                <li>Centralized and secured data</li>
                <li>Real-time orchestrated information flows</li>
                <li>End-to-end automation</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Actor Cards Section */}
      <section id="acteurs" className="section actors-overview-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Who uses EcoLunch?</h2>
            <p className="section-subtitle">
              5 types of users, 5 unique needs, 5 dedicated portals
            </p>
          </div>

          <div className="actors-grid-simple">
            <div className="actor-card-simple" onClick={() => navigate('/en/parents')}>
              <div className="actor-icon-simple parent-color">
                <Users size={40} />
              </div>
              <h3>Parents</h3>
              <p>Order meals for all your children in 15 seconds</p>
            </div>

            <div className="actor-card-simple" onClick={() => navigate('/en/caterers')}>
              <div className="actor-icon-simple caterer-color">
                <ChefHat size={40} />
              </div>
              <h3>Caterers</h3>
              <p>Automate your back-office and increase your revenue substantially</p>
            </div>

            <div className="actor-card-simple" onClick={() => navigate('/en/schools')}>
              <div className="actor-icon-simple school-color">
                <School size={40} />
              </div>
              <h3>Schools</h3>
              <p>Real-time dashboard to manage food satisfaction</p>
            </div>

            <div className="actor-card-simple" onClick={() => navigate('/en/directions')}>
              <div className="actor-icon-simple direction-color">
                <Building2 size={40} />
              </div>
              <h3>Boards</h3>
              <p>Consolidated multi-establishment view for governance</p>
            </div>

            <div className="actor-card-simple" onClick={() => navigate('/en/css')}>
              <div className="actor-icon-simple css-color">
                <Landmark size={40} />
              </div>
              <h3>CSS (HQ)</h3>
              <p>Global food governance and strategic policy</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why EcoLunch */}
      <section className="section" data-testid="why-ecolunch">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Why EcoLunch?</h2>
            <p className="section-subtitle">
              The first vertical SaaS infrastructure designed in Quebec, for Quebec realities
            </p>
          </div>

          <div className="pourquoi-grid">
            <div className="pourquoi-tile" data-testid="tile-quebec">
              <div className="portal-icon" style={{color: '#84CC16'}}>
                <MapPin size={30} />
              </div>
              <h3>Designed in Quebec, for Quebec</h3>
              <p>
                Built and deployed by Quebecers who directly understand the reality of schools, parents and caterers here.
                Not a generic tool hastily adapted — a platform built according to our workflows, field constraints and administrative realities.
              </p>
            </div>

            <div className="pourquoi-tile" data-testid="tile-founders">
              <div className="portal-icon" style={{color: '#FF8C00'}}>
                <ChefHat size={30} />
              </div>
              <h3>Free for Caterers (&lt;20 schools)</h3>
              <p>
                EcoLunch is <strong>completely free for any caterer serving fewer than 20 schools</strong>.
                No subscription, no support fees, no update fees.
                Full access to all features, updates and technical support included.
              </p>
            </div>

            <div className="pourquoi-tile" data-testid="tile-free">
              <div className="portal-icon" style={{color: '#F472B6'}}>
                <Gift size={30} />
              </div>
              <h3>Free for Schools and Parents</h3>
              <p>
                EcoLunch will be and remain free for schools — with the possibility of donation contributions.
                For parents, usage is also free. Access to the service must be simple, smooth and barrier-free.
              </p>
            </div>

            <div className="pourquoi-tile" data-testid="tile-connect">
              <div className="portal-icon" style={{color: '#22C55E'}}>
                <Rocket size={30} />
              </div>
              <h3>EcoLunch Connect — Growth Engine</h3>
              <p>
                We work with schools that want to offer hot meal services but don't yet do so.
                By eliminating operational complexity, we help schools get started and caterers expand their network.
              </p>
            </div>

            <div className="pourquoi-tile" data-testid="tile-coparenting">
              <div className="portal-icon" style={{color: '#00D4FF'}}>
                <HeartHandshake size={30} />
              </div>
              <h3>Integrated Co-parenting</h3>
              <p>
                Family realities are not uniform. EcoLunch integrates a co-parenting portal to manage ordering,
                payment and tracking responsibilities between multiple adults. A platform rooted in real life.
              </p>
            </div>

            <div className="pourquoi-tile" data-testid="tile-partners">
              <div className="portal-icon" style={{color: '#FACC15'}}>
                <Award size={30} />
              </div>
              <h3>Local and Credible Foundation</h3>
              <p>
                <strong>Sezzle Canada</strong> — flexible payments<br />
                <strong>Paysafe Montreal</strong> — secure processing<br />
                <strong>OVH Cloud Quebec</strong> — sovereign hosting<br /><br />
                Recognized partners for a solid infrastructure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-columns">
            <div className="footer-column">
              <div className="footer-brand">
                <div className="footer-logo">
                  <img src="/logo-ecolunch-full.png" alt="EcoLunch" />
                </div>
                <p>The unified platform for Quebec school meal management.</p>
              </div>
            </div>

            <div className="footer-column">
              <h4>Actors</h4>
              <a onClick={() => navigate('/en/parents')}>Parents</a>
              <a onClick={() => navigate('/en/schools')}>Schools</a>
              <a onClick={() => navigate('/en/caterers')}>Caterers</a>
              <a onClick={() => navigate('/en/directions')}>Boards</a>
              <a onClick={() => navigate('/en/css')}>CSS</a>
            </div>

            <div className="footer-column">
              <h4>Platform</h4>
              <a onClick={() => navigate('/en/platform')}>Platform</a>
              <a onClick={() => navigate('/en/coparenting')}>Co-Parenting</a>
                <a onClick={() => navigate('/en/ecopulse')}>EcoPulse CC</a>
              <a onClick={() => navigate('/en/ecoloop')}>EcoLoop</a>
            </div>

            <div className="footer-column">
              <h4>Contact</h4>
              <p>info@ecolunch.ca</p>
              <p>Montreal, QC, Canada</p>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2025 EcoLunch. All rights reserved.</p>
            <div className="footer-links">
              <a href="#">Privacy</a>
              <a href="#">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomeEN;
